import { useMemo } from "react";
import { quoteCalculatorData } from "../data/mockData";
import { DEFAULT_PRICING, LAUNDRY_SERVICE, CARPET_SERVICE, addMinutesToDuration } from "../lib/pricing";

const { durations } = quoteCalculatorData;

const pick = (list, index) => list[Math.min(Math.max(index, 0), list.length - 1)];

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const OVEN_LINES = [
  ["doubleOven", "Double oven"],
  ["agaRange", "AGA / range cooker"],
  ["hob", "Hob cleaning"],
  ["extractorFan", "Extractor fan"],
];

function laundryLines(laundry, rates) {
  const lines = [];
  if (laundry.kg) lines.push({ label: `${laundry.kg} kg wash, dry & fold`, amount: laundry.kg * rates.perKg });
  if (laundry.ironing) lines.push({ label: plural(laundry.ironing, "ironing item"), amount: laundry.ironing * rates.ironingItem });
  if (laundry.duvets) lines.push({ label: plural(laundry.duvets, "duvet"), amount: laundry.duvets * rates.duvet });
  if (laundry.suits) lines.push({ label: `${plural(laundry.suits, "2-piece suit")} dry cleaned`, amount: laundry.suits * rates.suitDryClean });
  return lines.length ? lines : [{ label: "Choose your laundry items", amount: 0 }];
}

function carpetLines(carpet, rates) {
  const lines = [];
  if (carpet.rooms) lines.push({ label: plural(carpet.rooms, "bedroom / room"), amount: carpet.rooms * rates.room });
  if (carpet.livingRooms) lines.push({ label: plural(carpet.livingRooms, "living room"), amount: carpet.livingRooms * rates.livingRoom });
  if (carpet.stairsHallways) lines.push({ label: `${carpet.stairsHallways} stairs / hallway${carpet.stairsHallways === 1 ? "" : "s"}`, amount: carpet.stairsHallways * rates.stairsHallway });
  if (carpet.stainProtection && lines.length) lines.push({ label: "Stain protection / pet odour treatment", amount: rates.stainProtection });
  return lines.length ? lines : [{ label: "Choose your carpet areas", amount: 0 }];
}

function baseLinesFor(service, bedroomIndex, ovenOptions, squareFootage, pricing, laundry, carpet) {
  if (service === CARPET_SERVICE) {
    return carpetLines(carpet, pricing.carpet || DEFAULT_PRICING.carpet);
  }
  if (service === LAUNDRY_SERVICE) {
    return laundryLines(laundry, pricing.laundry || DEFAULT_PRICING.laundry);
  }
  if (service === "Oven Cleaning") {
    const { oven } = pricing;
    const lines = [{ label: "Oven cleaning (base)", amount: oven.base }];
    OVEN_LINES.forEach(([key, label]) => {
      if (ovenOptions[key]) lines.push({ label, amount: oven[key] });
    });
    return lines;
  }
  if (service === "Commercial Cleaning") {
    const extraThousands = Math.max(0, Math.floor((squareFootage - 1000) / 1000));
    const lines = [{ label: "Commercial (up to 1,000 sq ft)", amount: pricing.commercial.base }];
    if (extraThousands) {
      lines.push({
        label: `Extra ${(extraThousands * 1000).toLocaleString()} sq ft`,
        amount: extraThousands * pricing.commercial.perExtra1000SqFt,
      });
    }
    return lines;
  }
  const prices = pricing.basePrices[service] || pricing.basePrices["Regular Cleaning"];
  const beds = Math.min(bedroomIndex, prices.length - 1) + 1;
  return [{ label: `${service} · ${plural(beds, "bedroom")}`, amount: pick(prices, bedroomIndex) }];
}

function baseDurationFor(service, bedroomIndex, ovenOptions, squareFootage) {
  if (service === LAUNDRY_SERVICE) return "24-48 hours turnaround";
  if (service === CARPET_SERVICE) return "2-4 hours drying time";
  if (service === "Oven Cleaning") {
    const itemCount = Object.values(ovenOptions).filter(Boolean).length;
    if (itemCount <= 1) return "1-2 hours";
    if (itemCount === 2) return "2-3 hours";
    return "3-4 hours";
  }
  if (service === "Commercial Cleaning") {
    if (squareFootage <= 2000) return "2-3 hours";
    if (squareFootage <= 4000) return "4-5 hours";
    if (squareFootage <= 6000) return "6-7 hours";
    return "8+ hours";
  }
  return pick(durations[service] || durations["Regular Cleaning"], bedroomIndex);
}

/**
 * Quote price and duration calculation driven by admin-editable pricing.
 */
export function useQuoteCalculator(
  selectedService,
  bedrooms,
  frequency,
  ovenOptions = {},
  squareFootage = 1000,
  bathrooms = 1,
  toilets = 0,
  pricing = DEFAULT_PRICING,
  laundry = {},
  carpet = {}
) {
  const bedroomIndex = Math.max(0, bedrooms - 1);

  return useMemo(() => {
    const extras = pricing.roomExtras[selectedService];
    const extraBathrooms = Math.max(0, bathrooms - 1);
    const extraToilets = Math.max(0, toilets);

    const lines = baseLinesFor(selectedService, bedroomIndex, ovenOptions, squareFootage, pricing, laundry, carpet);
    if (extras && extraBathrooms) {
      lines.push({ label: plural(extraBathrooms, "extra bathroom"), amount: extraBathrooms * extras.bathroom });
    }
    if (extras && extraToilets) {
      lines.push({ label: plural(extraToilets, "separate toilet"), amount: extraToilets * extras.toilet });
    }
    const subtotal = lines.reduce((sum, l) => sum + l.amount, 0);
    const discountPct = pricing.discounts[frequency] || 0;
    const estimatedPrice = Math.round(subtotal * (1 - discountPct / 100));
    const breakdown = { lines, discountPct, discount: subtotal - estimatedPrice, frequency };

    const extraMinutes = extras
      ? extraBathrooms * pricing.timeExtras.bathroomMinutes + extraToilets * pricing.timeExtras.toiletMinutes
      : 0;
    const estimatedDuration = addMinutesToDuration(
      baseDurationFor(selectedService, bedroomIndex, ovenOptions, squareFootage),
      extraMinutes
    );

    return { estimatedPrice, estimatedDuration, bedroomIndex, breakdown };
  }, [selectedService, bedroomIndex, frequency, ovenOptions, squareFootage, bathrooms, toilets, pricing, laundry, carpet]);
}
